import { useTheme } from "@emotion/react";
import * as Popover from "@radix-ui/react-popover";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";

type Option = {
  value: string;
  label: string;
};

export type SelectDropdownProps = {
  options: Option[];
  onChange: (selectedValue: string) => void;
  value?: string | null;
  className?: string;
  placeholder?: string;
  isDisabled?: boolean;
  allowEmptySelection?: boolean;
  trigger?: React.ReactNode;
};

export const SelectDropdown: React.FC<SelectDropdownProps> = (props) => {
  const theme = useTheme();
  const triggerId = useId();
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const highlightedIndexRef = useRef(0);
  const [panelWidth, setPanelWidth] = useState<number | undefined>();
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const selectableRows = useMemo(() => {
    const rows: Option[] = [];
    if (props.allowEmptySelection) {
      rows.push({
        value: "",
        label: props.placeholder ?? "Wybierz ...",
      });
    }
    rows.push(...props.options);
    return rows;
  }, [props.allowEmptySelection, props.options, props.placeholder]);

  const selectedOption = props.value ? props.options.find((o) => o.value === props.value) : undefined;
  const displayLabel = selectedOption?.label ?? props.placeholder ?? "Wybierz ...";

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && triggerRef.current) {
      setPanelWidth(triggerRef.current.offsetWidth);
    }
  };

  useEffect(() => {
    if (!open) return;
    const normalizedValue = props.value ?? "";
    const idx = selectableRows.findIndex((row) => row.value === normalizedValue);
    const next = idx >= 0 ? idx : 0;
    highlightedIndexRef.current = next;
    setHighlightedIndex(next);
  }, [open, selectableRows, props.value]);

  const pick = (nextValue: string) => {
    props.onChange(nextValue);
    setOpen(false);
  };

  const rowHover = {
    background: theme.colors.hover,
  };

  const handleListKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const lastIndex = Math.max(0, selectableRows.length - 1);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = Math.min(lastIndex, highlightedIndexRef.current + 1);
      highlightedIndexRef.current = next;
      setHighlightedIndex(next);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      const next = Math.max(0, highlightedIndexRef.current - 1);
      highlightedIndexRef.current = next;
      setHighlightedIndex(next);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      highlightedIndexRef.current = 0;
      setHighlightedIndex(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      highlightedIndexRef.current = lastIndex;
      setHighlightedIndex(lastIndex);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const row = selectableRows[highlightedIndexRef.current];
      if (row) {
        pick(row.value);
      }
    }
  };

  const activeDescendantId =
    selectableRows.length > 0 ? `${listboxId}-opt-${highlightedIndex}` : undefined;

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          id={triggerId}
          ref={triggerRef}
          disabled={props.isDisabled}
          className={props.className}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          css={
            props.trigger
              ? {
                  display: "flex",
                  padding: 0,
                  border: "none",
                  background: "none",
                  borderRadius: theme.smallBorderRadius,
                }
              : {
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.5rem",
                  padding: "0.4375rem 0.5rem",
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.smallBorderRadius,
                  backgroundColor: theme.colors.background,
                  fontSize: "1rem",
                  color: selectedOption ? theme.colors.primary : "#757575",
                  cursor: props.isDisabled ? "not-allowed" : "pointer",
                  opacity: props.isDisabled ? 0.6 : 1,
                  ":focus-visible": {
                    outlineStyle: "solid",
                    outlineColor: theme.colors.primary,
                    outlineWidth: "1px",
                  },
                }
          }
        >
          {props.trigger ?? (
            <>
              <span
                css={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "left",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {displayLabel}
              </span>
              <MdKeyboardArrowDown size={20} color={theme.colors.secondaryText} />
            </>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            window.requestAnimationFrame(() => {
              listRef.current?.focus();
            });
          }}
          css={{
            zIndex: 10000,
            borderRadius: theme.smallBorderRadius,
            backgroundColor: theme.colors.background,
            border: `1px solid ${theme.colors.border}`,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            padding: 0,
            minWidth: panelWidth ? `${Math.max(panelWidth, 120)}px` : undefined,
          }}
        >
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            tabIndex={0}
            aria-labelledby={triggerId}
            aria-activedescendant={activeDescendantId}
            onKeyDown={handleListKeyDown}
            css={{
              maxHeight: "min(50vh, 280px)",
              overflowY: "auto",
              padding: "0.25rem",
              ":focus-visible": {
                outlineStyle: "none",
              },
            }}
          >
            {selectableRows.map((row, index) => {
              const normalizedValue = props.value ?? "";
              const isSelected = row.value === normalizedValue;
              const isHighlighted = index === highlightedIndex;
              const optionId = `${listboxId}-opt-${index}`;
              return (
                <div
                  key={row.value === "" ? "__empty__" : row.value}
                  id={optionId}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => {
                    highlightedIndexRef.current = index;
                    setHighlightedIndex(index);
                  }}
                  onClick={() => pick(row.value)}
                  css={{
                    width: "100%",
                    display: "block",
                    textAlign: "left",
                    padding: "0.5rem 0.75rem",
                    borderRadius: theme.smallBorderRadius,
                    boxShadow: isHighlighted ? `inset 0 0 0 2px ${theme.colors.primary}` : undefined,
                    boxSizing: "border-box",
                    background: isSelected ? theme.colors.primaryLight : "transparent",
                    color: theme.colors.primary,
                    fontSize: "0.9375rem",
                    fontWeight: isSelected ? 700 : 400,
                    cursor: "pointer",
                    ":hover": rowHover,
                  }}
                >
                  {row.label}
                </div>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
